import { z } from 'zod';
import { defineRoute } from '../core/contract';
import { IdZ, OkZ, qBool, TsZ } from '../schemas/common';
import { MemberPatchZ, MemberZ } from '../schemas/member';
import { PlayerCreateZ, PlayerDetailZ, PlayerListItemZ, PlayerPatchZ, PlayerZ } from '../schemas/player';
import { CrewPatchZ, CrewZ } from '../schemas/settings';

/** Owner: players (roster, guests, invites, crew admin, profile). */

export const InviteZ = z.object({
  id: IdZ,
  kind: z.enum(['crew', 'personal']),
  /** Crew code: always shown to members. Personal code: only in the create response. */
  code: z.string().nullable(),
  /** https://host/j/<code>, for the QR. null when the code is not shown. */
  url: z.string().nullable(),
  playerId: IdZ.nullable(),
  createdBy: IdZ,
  createdAt: TsZ,
  expiresAt: TsZ.nullable(),
  maxUses: z.number().int().positive().nullable(),
  uses: z.number().int().nonnegative(),
  revokedAt: TsZ.nullable(),
});
export type Invite = z.infer<typeof InviteZ>;

export const listPlayers = defineRoute({
  name: 'players.list',
  method: 'GET',
  path: '/api/players',
  auth: 'mem',
  query: z.object({
    includeArchived: qBool.optional(),
    includeGuests: qBool.optional(),
  }),
  res: z.object({ items: z.array(PlayerListItemZ) }),
});

export const createPlayer = defineRoute({
  name: 'players.create',
  method: 'POST',
  path: '/api/players',
  auth: 'memw',
  body: PlayerCreateZ,
  res: PlayerZ,
  status: 201,
  idempotent: true,
  summary: 'Adds a guest (a cousin with no phone) or a player a personal invite will bind to.',
});

export const getPlayer = defineRoute({
  name: 'players.get',
  method: 'GET',
  path: '/api/players/:id',
  auth: 'mem',
  res: PlayerDetailZ,
  errors: ['NOT_FOUND'],
});

export const patchPlayer = defineRoute({
  name: 'players.patch',
  method: 'PATCH',
  path: '/api/players/:id',
  auth: 'mem',
  body: PlayerPatchZ,
  res: PlayerZ,
  errors: ['NOT_FOUND', 'FORBIDDEN'],
  summary: 'Self, any member for a guest, or the owner.',
});

export const archivePlayer = defineRoute({
  name: 'players.archive',
  method: 'POST',
  path: '/api/players/:id/archive',
  auth: 'own',
  res: PlayerZ,
});

export const unarchivePlayer = defineRoute({
  name: 'players.unarchive',
  method: 'POST',
  path: '/api/players/:id/unarchive',
  auth: 'own',
  res: PlayerZ,
});

export const getCrew = defineRoute({
  name: 'crew.get',
  method: 'GET',
  path: '/api/crew',
  auth: 'mem',
  res: CrewZ,
});

export const patchCrew = defineRoute({
  name: 'crew.patch',
  method: 'PATCH',
  path: '/api/crew',
  auth: 'own',
  body: CrewPatchZ,
  res: CrewZ,
});

export const listMembers = defineRoute({
  name: 'crew.listMembers',
  method: 'GET',
  path: '/api/crew/members',
  auth: 'mem',
  res: z.object({ items: z.array(MemberZ) }),
});

export const patchMember = defineRoute({
  name: 'crew.patchMember',
  method: 'PATCH',
  path: '/api/crew/members/:id',
  auth: 'own',
  body: MemberPatchZ,
  res: MemberZ,
  errors: ['NOT_FOUND', 'CONFLICT'],
  summary: 'Role change or disable. The last owner cannot be demoted (CONFLICT).',
});

export const getInvites = defineRoute({
  name: 'crew.getInvites',
  method: 'GET',
  path: '/api/crew/invites',
  auth: 'mem',
  res: z.object({ crew: InviteZ.nullable(), personal: z.array(InviteZ) }),
});

export const rotateCrewCode = defineRoute({
  name: 'crew.rotateCrewCode',
  method: 'POST',
  path: '/api/crew/invites/rotate',
  auth: 'own',
  body: z.object({ expiresInDays: z.number().int().min(1).max(365).nullable().optional() }),
  res: InviteZ,
  summary: 'New crew code. The old one stops working; joined devices stay in.',
});

export const createPersonalInvite = defineRoute({
  name: 'crew.createPersonalInvite',
  method: 'POST',
  path: '/api/crew/invites/personal',
  auth: 'memw',
  body: z.object({ id: IdZ, playerId: IdZ }),
  res: InviteZ,
  status: 201,
  idempotent: true,
  errors: ['NOT_FOUND', 'CONFLICT'],
  summary: 'One-use code bound to an existing player, so their logged stats land on them.',
});

export const revokeInvite = defineRoute({
  name: 'crew.revokeInvite',
  method: 'DELETE',
  path: '/api/crew/invites/:id',
  auth: 'own',
  res: OkZ,
});
